<template>
  <!--Login Form-->
  <el-form
    @submit.native.prevent
    labelPosition="top">
    <el-form-item
      v-test-id="'login-username'"
      :label="selectedOAuthProvider ? 'Email' : 'Username'"
      prop="username"
      hide-asterisk>
      <el-input
        v-validate="'required|bc-no-space'"
        :value="username"
        @input="$emit('update:username', $event)"
        data-vv-delay="1"
        name="username">
      </el-input>
    </el-form-item>

    <el-form-item
      v-if="!selectedOAuthProvider"
      v-test-id="'login-password'"
      label="Password"
      prop="password"
      hide-asterisk>
      <el-input-password
        v-model="password"
        v-validate="'required'"
        name="password">
      </el-input-password>
    </el-form-item>

    <el-form-item class="u-mb3">
      <div v-if="!selectedOAuthProvider">
        <el-button
          v-if="!isUsingIWSCredentials"
          v-test-id="'regular-sign-in-button'"
          type="primary"
          @click="validateAndSignIn(login)"
          native-type="submit"
          class="u-mt3"
          block>Sign In</el-button>
          <el-alert
            v-else
            title=""
            type="warning"
            closable>
              <div class="el-alert__title">
                We're sorry, but login via 'IWS_' credentials is no longer supported. Please login
                <a href='https://britecore.okta.com/'>
                  via Okta
                </a>
                (using company email).
              </div>
          </el-alert>
      </div>

      <div
        v-for="provider of providersWithVisibleSignInWithButton"
        :key="provider.name">
        <el-button
          v-test-id="'external-provider-sign-in-button'"
          type="primary"
          @click="signInWith(provider.name)"
          native-type="submit"
          class="u-mt3"
          block>
          {{ provider.label }}
        </el-button>
      </div>

      <div
        v-if="providerIsEnabled('Google')"
        class="u-mt2 l-flex-grid">
        <el-button
          type="primary"
          v-if="providerIsEnabled('Google')"
          @click="signInWith('Google')"
          native-type="submit"
          class="c-login__sso-button c-login__sso-button--google"
          size="small"
          :icon="['fab', 'google']">
          Sign In with Google
        </el-button>
      </div>
    </el-form-item>

    <router-link
      :to="{name: 'forgot-password'}"
      :username="username"
      class="u-text--center u-block helper-text">Forgot your password?
    </router-link>
  </el-form>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'
import { ErrorsMixin } from '@/components/ui/mixins'

export default {
  mixins: [ErrorsMixin],
  props: {
    username: {
      type: String,
      required: true
    },
    preferences: {
      type: Object,
      required: false
    }
  },
  data: function () {
    return {
      password: ''
    }
  },
  created () {
    const preSelectedIdentityProvider = this.login.preSelectedIdentityProvider

    if (preSelectedIdentityProvider) {
      this.signInWith(preSelectedIdentityProvider)
    }
  },
  computed: {
    login: function () {
      return {
        credentials: {
          username: this.username,
          password: this.password
        },
        preferences: this.preferences,
        redirectPath: this.$route.query.redirect,
        preSelectedIdentityProvider: this.$route.query.identityProviderName
      }
    },

    isUsingIWSCredentials: function () {
      return _.startsWith(this.username.toUpperCase(), 'IWS_')
    },

    selectedOAuthProvider: function () {
      const allOAuthProviders = this.$store.getters['authentication/oauthProviders']

      for (const provider of allOAuthProviders) {
        const providerEmailDomain = provider.domain
        if (providerEmailDomain && this.username.endsWith(`@${providerEmailDomain}`)) {
          return provider
        }
      }

      return null
    },

    providersWithVisibleSignInWithButton: function () {
      const currentlySelectedProvider = this.selectedOAuthProvider
      if (currentlySelectedProvider) {
        return [currentlySelectedProvider]
      }

      return this.$store.getters['authentication/oauthProviders'].filter(provider => provider.showSignInWithButton)
    },

    ...mapGetters('authentication', [
      'isAuthenticated',
      'providerIsEnabled',
      'lastSignInError'
    ]),
    ...mapState('authentication', [
      'lastSignInError'
    ])
  },
  methods: {
    validateAndSignIn: async function (...args) {
      try {
        let isValid = await this.$validator.validateAll()
        if (isValid) {
          await this.$store.dispatch('authentication/signIn', ...args)
        }
      } catch (e) {
        this.$message.error('We\'re sorry, but some information is missing.')
      }
    },
    signInWith: function (providerName) {
      this.$store.dispatch('authentication/signInWith', { providerName, redirectPath: this.login.redirectPath })
    }
  },
  watch: {
    lastSignInError: function (error) {
      this.$message.error(error.message)
    }
  }
}
</script>
